/**
 * PROTOTYPE variant B: rail and ledger.
 *
 * Two panes. The left rail lists every machine with its presence dot, so
 * switching targets is one click and "what else is there" needs no menu.
 * The right pane is a ledger: one row per check, grouped by the source that
 * asserted it, with the result and the time it was observed. Hairlines here
 * are informational: this is a table, and rows are records.
 */
import { cn } from "../../../lib/utils";
import { StatusDot, StatusPill, formatTimestamp } from "../DiagnosticsPanelPrimitives";
import type { PrototypeCheck } from "./diagnosticsPrototypeModel";
import { PrototypeRefreshButton } from "./PrototypeRefreshButton";
import type { DiagnosticsPrototypeVariantProps } from "./diagnosticsPrototypeProps";

function groupChecks(
  checks: ReadonlyArray<PrototypeCheck>,
): ReadonlyArray<{ readonly group: string; readonly rows: ReadonlyArray<PrototypeCheck> }> {
  const order: string[] = [];
  const byGroup = new Map<string, PrototypeCheck[]>();
  for (const check of checks) {
    if (!byGroup.has(check.group)) {
      byGroup.set(check.group, []);
      order.push(check.group);
    }
    byGroup.get(check.group)!.push(check);
  }
  return order.map((group) => ({ group, rows: byGroup.get(group) ?? [] }));
}

export function DiagnosticsPrototypeVariantB({
  model,
  targets,
  refresh,
}: DiagnosticsPrototypeVariantProps) {
  const grouped = groupChecks(model.checks);
  return (
    <div className="grid gap-8 px-3 sm:px-4 md:grid-cols-[13rem_minmax(0,1fr)]">
      <nav aria-label="Diagnostics targets" className="md:sticky md:top-4 md:self-start">
        {targets.groups.map((group) => (
          <div key={group.id} className="mb-5">
            <p className="mb-1 text-xs text-muted-foreground">{group.label}</p>
            <ul className="space-y-0.5">
              {group.options.map((option) => {
                const selected = targets.active?.key === option.key;
                return (
                  <li key={option.key}>
                    <button
                      type="button"
                      onClick={() => targets.onSelect(option)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm",
                        selected
                          ? "bg-accent text-foreground"
                          : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
                      )}
                    >
                      <StatusDot tone={option.tone} />
                      <span className="min-w-0 flex-1 truncate">{option.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        <p className="text-xs text-muted-foreground">
          {model.events.length === 0 ? "No recent events" : `${model.events.length} recent events`}
        </p>
      </nav>

      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-lg font-semibold tracking-[-0.025em] text-foreground">
            {model.target?.label ?? "Select a target"}
          </h3>
          <StatusPill label={model.verdict.label} tone={model.verdict.tone} />
          <PrototypeRefreshButton refresh={refresh} />
        </div>
        <p className="mt-1 text-sm text-muted-foreground">{model.verdict.sentence}</p>
        {model.verdict.disagreement ? (
          <p className="mt-2 text-sm text-warning-foreground">{model.verdict.disagreement}</p>
        ) : null}

        <table className="mt-6 w-full border-collapse text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className="py-1.5 pr-4 font-medium">Check</th>
              <th className="py-1.5 pr-4 font-medium">Result</th>
              <th className="py-1.5 text-right font-medium">Observed</th>
            </tr>
          </thead>
          {grouped.map((section) => (
            <tbody key={section.group}>
              <tr>
                <th
                  colSpan={3}
                  className="pt-5 pb-1.5 text-left text-xs font-medium text-foreground"
                >
                  {section.group}
                </th>
              </tr>
              {section.rows.map((row) => (
                <tr key={`${section.group}-${row.name}`} className="border-t border-border/60">
                  <td className="py-2 pr-4 text-muted-foreground">{row.name}</td>
                  <td className="py-2 pr-4">
                    <span className="inline-flex items-center gap-2 text-foreground">
                      <StatusDot tone={row.tone} />
                      <span className="truncate">{row.result}</span>
                    </span>
                  </td>
                  <td className="py-2 text-right text-xs text-muted-foreground">
                    {formatTimestamp(row.observedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          ))}
          {model.events.length > 0 ? (
            <tbody>
              <tr>
                <th
                  colSpan={3}
                  className="pt-5 pb-1.5 text-left text-xs font-medium text-foreground"
                >
                  Events
                </th>
              </tr>
              {model.events.map((event) => (
                <tr key={`${event.title}-${event.at}`} className="border-t border-border/60">
                  <td className="py-2 pr-4 text-muted-foreground">{event.title}</td>
                  <td className="py-2 pr-4">
                    <span className="inline-flex items-center gap-2 text-foreground">
                      <StatusDot tone={event.tone} />
                      <span className="truncate">{event.detail}</span>
                    </span>
                  </td>
                  <td className="py-2 text-right text-xs text-muted-foreground">
                    {formatTimestamp(event.at)}
                  </td>
                </tr>
              ))}
            </tbody>
          ) : null}
        </table>
      </div>
    </div>
  );
}
